import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import { auditLogsApi } from '../../api/audit-logs';
import { AuditLog, PaginatedResponse } from '../../types';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { Pagination } from '../../components/ui/Pagination';
import { Drawer } from '../../components/ui/Drawer';
import { Badge } from '../../components/ui/Badge';
import {
  DateRangeFilter,
  DateRange,
  DateRangePreset,
  computeRange,
} from '../../components/ui/DateRangeFilter';
import { useToast } from '../../hooks/useToast';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const formatAction = (action: string) =>
  action
    .split('.')
    .map((w) => w.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()))
    .join(' — ');

const actionColor = (action: string): 'danger' | 'warning' | 'success' | 'info' | 'neutral' => {
  if (action.includes('delete') || action.includes('suspend') || action.includes('disable'))
    return 'danger';
  if (action.includes('update') || action.includes('patch') || action.includes('toggle'))
    return 'warning';
  if (action.includes('create') || action.includes('enable') || action.includes('activate'))
    return 'success';
  if (action.includes('login') || action.includes('view') || action.includes('export'))
    return 'info';
  return 'neutral';
};

export const AuditLogs: React.FC = () => {
  const [search, setSearch] = useState('');
  const toast = useToast();

  usePage('Audit Logs', {
    placeholder: 'Search by admin email or action…',
    onSearch: (val) => setSearch(val),
  });

  const [data, setData] = useState<PaginatedResponse<AuditLog> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState<DateRange>(() => computeRange('this_month'));
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const result = await auditLogsApi.getAuditLogs({
        adminEmail: search || undefined,
        fromDate: dateRange.fromDate.toISOString(),
        toDate: dateRange.toDate.toISOString(),
        page,
        limit: 25,
      });
      setData(result);
    } catch (err) {
      toast.error(err, 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [search, dateRange, page, toast]);

  useEffect(() => {
    setPage(1);
  }, [search, dateRange]);

  useEffect(() => {
    const timer = setTimeout(() => void fetchLogs(), 250);
    return () => clearTimeout(timer);
  }, [fetchLogs]);

  const handleRangeChange = useCallback(
    (_preset: DateRangePreset, range: DateRange) => setDateRange(range),
    [],
  );

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            All state-changing admin actions are recorded here. Read-only.
          </p>
          <DateRangeFilter defaultPreset="this_month" onChange={handleRangeChange} />
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Timestamp', 'Admin', 'Action', 'Target', 'IP'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--neutral-600)',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          borderBottom: '1px solid var(--neutral-300)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: '48px 16px',
                          textAlign: 'center',
                          color: 'var(--neutral-400)',
                        }}
                      >
                        No audit logs in this period
                      </td>
                    </tr>
                  ) : (
                    data.items.map((log, i) => (
                      <tr
                        key={log.id}
                        onClick={() => setSelectedLog(log)}
                        style={{
                          background:
                            i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--neutral-300)',
                          transition: 'background 150ms ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--blue-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)';
                        }}
                      >
                        <td
                          style={{
                            padding: '12px 16px',
                            color: 'var(--neutral-600)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDate(log.createdAt)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 500, color: 'var(--neutral-800)' }}>
                            {log.adminEmail ?? 'System'}
                          </div>
                          {log.adminRole && (
                            <div style={{ fontSize: 11, color: 'var(--neutral-500)' }}>
                              {log.adminRole.replace('_', ' ')}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Badge variant={actionColor(log.action)}>
                            {formatAction(log.action)}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--neutral-600)' }}>
                          {log.targetType && (
                            <span>
                              {log.targetType}
                              {log.targetId && (
                                <span
                                  style={{
                                    fontFamily: 'monospace',
                                    fontSize: 11,
                                    color: 'var(--neutral-400)',
                                    marginLeft: 6,
                                  }}
                                >
                                  {log.targetId.slice(0, 8)}…
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            color: 'var(--neutral-500)',
                          }}
                        >
                          {log.ipAddress ?? '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      <Drawer
        isOpen={selectedLog !== null}
        onClose={() => setSelectedLog(null)}
        title={selectedLog ? formatAction(selectedLog.action) : ''}
        subtitle={selectedLog ? formatDate(selectedLog.createdAt) : ''}
        width={560}
      >
        {selectedLog && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { label: 'Log ID', value: selectedLog.id },
              { label: 'Admin', value: selectedLog.adminEmail ?? 'System' },
              { label: 'Role', value: selectedLog.adminRole ?? '—' },
              { label: 'Action', value: selectedLog.action },
              { label: 'Target Type', value: selectedLog.targetType ?? '—' },
              { label: 'Target ID', value: selectedLog.targetId ?? '—' },
              { label: 'IP Address', value: selectedLog.ipAddress ?? '—' },
              { label: 'Timestamp', value: formatDate(selectedLog.createdAt) },
            ].map((row, i) => (
              <div
                key={row.label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid var(--neutral-200)',
                  background: i % 2 === 0 ? 'transparent' : 'transparent',
                }}
              >
                <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>
                  {row.label}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: 'var(--neutral-800)',
                    fontFamily:
                      row.label.includes('ID') || row.label === 'IP Address'
                        ? 'monospace'
                        : 'inherit',
                    wordBreak: 'break-all',
                  }}
                >
                  {row.value}
                </span>
              </div>
            ))}

            {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--neutral-500)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    marginTop: 20,
                    marginBottom: 8,
                  }}
                >
                  Changes
                </h3>
                <pre
                  style={{
                    fontSize: 12,
                    padding: 12,
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    overflowX: 'auto',
                    fontFamily: 'monospace',
                    color: 'var(--neutral-700)',
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {JSON.stringify(selectedLog.changes, null, 2)}
                </pre>
              </>
            )}

            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--neutral-500)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    marginTop: 20,
                    marginBottom: 8,
                  }}
                >
                  Metadata
                </h3>
                <pre
                  style={{
                    fontSize: 12,
                    padding: 12,
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    overflowX: 'auto',
                    fontFamily: 'monospace',
                    color: 'var(--neutral-700)',
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};